// Whole-document photo attachments (backlog DOCS-20). Adapted from the
// Lovable design kit's reference implementation (files/snapdraw-design-kit/
// code/src/components/snapdraw/photo-attachments.tsx) with real Storage
// upload wiring in place of the reference's URL.createObjectURL-only demo,
// and with the `orientation` field dropped entirely — the reference never
// set it correctly (always hardcoded "landscape") and it becomes
// unnecessary once print tiles use object-contain (DocumentViewPage.tsx),
// which handles any aspect ratio without needing to know it in advance.
import { useEffect, useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { AlertTriangle, Camera, ChevronDown, ChevronUp, ImagePlus, Printer, Share, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Capacitor } from "@capacitor/core";
import { Camera as NativeCamera } from "@capacitor/camera";

import { useT } from "@/lib/i18n";
import { triggerDownload } from "@/lib/download";
import { MAX_PHOTOS, UnsupportedImageError, uploadDocPhoto } from "@/lib/photoUpload";
import { useResolvedPhotoUrls } from "@/lib/useResolvedPhotoUrls";
import type { DocPhoto } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * Fetches a photo's already-resolved URL back into bytes and hands it to
 * the same native-Share/web-download mechanism the PDF export already uses
 * (src/lib/download.ts) — a user-initiated "give me a copy on this device"
 * action, distinct from the in-app camera capture which deliberately does
 * NOT auto-save to the device gallery (backlog MOBILE-4's own notes: two
 * separate concerns, discussed and settled with the user before building
 * this). Shared by both the builder's editable tile (PhotoRow below) and
 * the read-only document-view sheet (quote-sheet.tsx's PhotoSheetSection).
 */
async function savePhotoToDevice(url: string, filename: string, onError: () => void) {
  try {
    const blob = await (await fetch(url)).blob();
    await triggerDownload(filename, blob);
  } catch (e) {
    console.error("Save photo to device failed:", e);
    onError();
  }
}

/**
 * One-time discovery hint for the save-to-device icon (a hover-revealed-on-
 * desktop/always-on-mobile affordance has no other way to be found) — shown
 * once, the first time a document with at least one real photo renders,
 * never again. Plain localStorage: purely a per-device UI nicety, not data
 * that needs to sync or be recoverable, so it doesn't need the account-
 * level persistence a real setting would.
 */
const SAVE_HINT_KEY = "snapdraw.seenPhotoSaveHint";
function showSaveHintOnce(message: string) {
  try {
    if (localStorage.getItem(SAVE_HINT_KEY)) return;
    localStorage.setItem(SAVE_HINT_KEY, "1");
  } catch {
    // Private-browsing/storage-blocked: just skip the one-time hint rather
    // than fail the render over a nicety.
    return;
  }
  toast(message);
}

/** Swaps a photo one position earlier/later; unchanged at the bounds. */
export function movePhoto(photos: DocPhoto[], index: number, delta: number): DocPhoto[] {
  const target = index + delta;
  if (target < 0 || target >= photos.length) return photos;
  const next = [...photos];
  const [moved] = next.splice(index, 1);
  next.splice(target, 0, moved!);
  return next;
}

type PendingUpload = {
  localId: string;
  file: File;
  previewUrl: string;
  // "unsupported": the browser couldn't decode this file as an image at
  // all (DOCS-37) — retrying would just fail identically, so this status
  // skips the Retry action and points the user at the actual fix instead.
  // "converting": the slower client-side HEIC fallback is running (can be
  // 20+ seconds on older hardware, confirmed via real-device testing) —
  // distinct from "uploading" so the wait doesn't read as a stuck/broken
  // upload.
  status: "uploading" | "converting" | "error" | "unsupported";
};

/**
 * Photo attachments — the one place in the builder where the contractor
 * adds something that is *only* visual. Everything else in this app
 * distinguishes editor-only affordances from what prints, so this card
 * wears the promise on its face: a PRINTS ON THE PDF chip in the header,
 * repeated as a hairline caption on every tile.
 *
 * Reorder is up/down, not drag: this is used one-handed on a phone at a
 * job site, where a drag handle inside a scrolling page is a coin flip.
 *
 * Uploads happen immediately on file-select, not deferred to Save — a
 * local blob preview shows right away, the real upload runs in the
 * background, and onChange (lifting the photo into the saved document)
 * only fires once the upload actually succeeds. A pending or failed
 * upload lives entirely in this component's own state and never reaches
 * the parent.
 */
export function PhotoAttachments({
  photos,
  onChange,
  /** Base filename for "save to device" (e.g. a slugified doc number) — matches the PDF export's own naming rule. Falls back to "photo" if the document has no number yet (a brand-new, unsaved draft). */
  docFilenamePrefix,
}: {
  photos: DocPhoto[];
  onChange: (next: DocPhoto[]) => void;
  docFilenamePrefix?: string;
}) {
  const t = useT();
  const fileRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [pending, setPending] = useState<PendingUpload[]>([]);
  const resolvedUrls = useResolvedPhotoUrls(photos);
  const full = photos.length + pending.length >= MAX_PHOTOS;

  const hasPhotos = photos.length > 0;
  useEffect(() => {
    // Only ever needs to fire once per device, the first time any document
    // with a real photo renders — not tied to this document's own identity.
    if (hasPhotos) showSaveHintOnce(t("photos.saveHint"));
  }, [hasPhotos, t]);

  // Concurrent uploads each resolve at a different time — reading `photos`
  // via a ref (not the closed-over prop) at the moment each one lands
  // avoids one upload's onChange silently clobbering another's.
  const photosRef = useRef(photos);
  useEffect(() => {
    photosRef.current = photos;
  }, [photos]);

  function startUpload(file: File) {
    const localId = crypto.randomUUID();
    const previewUrl = URL.createObjectURL(file);
    setPending((prev) => [...prev, { localId, file, previewUrl, status: "uploading" }]);

    uploadDocPhoto(file, () => {
      setPending((prev) => prev.map((p) => (p.localId === localId ? { ...p, status: "converting" } : p)));
    })
      .then((photo) => {
        setPending((prev) => prev.filter((p) => p.localId !== localId));
        onChange([...photosRef.current, photo]);
        URL.revokeObjectURL(previewUrl);
      })
      .catch((e) => {
        console.error("Photo upload failed:", e);
        const status = e instanceof UnsupportedImageError ? "unsupported" : "error";
        setPending((prev) => prev.map((p) => (p.localId === localId ? { ...p, status } : p)));
      });
  }

  /**
   * Native-only: a real `Camera.takePhoto()` call, guaranteed to launch the
   * device camera directly (backlog MOBILE-4). The plain web
   * `<input capture="environment">` hint below is only a suggestion — since
   * Android 13's unified Photo Picker, many WebView versions show the
   * general picker instead, found live testing on a real Android tablet.
   */
  async function takeNativePhoto() {
    try {
      const photo = await NativeCamera.takePhoto({});
      if (!photo.webPath) return;
      const blob = await (await fetch(photo.webPath)).blob();
      const file = new File([blob], `photo-${crypto.randomUUID()}.jpg`, { type: blob.type || "image/jpeg" });
      startUpload(file);
    } catch (e) {
      // The user backing out of the native camera UI is a normal, deliberate
      // action, not a failure — both iOS and Android's Camera plugin reject
      // with this exact, stable string on cancel (verified against
      // @capacitor/camera's own source for both platforms).
      if (e instanceof Error && e.message === "User cancelled photos app") return;
      console.error("Camera capture failed:", e);
      toast.error(t("photos.cameraError"));
    }
  }

  function addFiles(list: FileList | null) {
    if (!list || list.length === 0) return;
    const room = MAX_PHOTOS - (photos.length + pending.length);
    const files = Array.from(list)
      .filter((file) => file.type.startsWith("image/"))
      .slice(0, Math.max(room, 0));
    if (files.length === 0) {
      if (room <= 0) toast(t("photos.full"));
      return;
    }
    files.forEach(startUpload);
    toast(files.length === 1 ? t("photos.addedSingular") : t("photos.addedPlural", { count: files.length }));
  }

  return (
    <section className="sd-card p-5 lg:p-7">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-display text-2xl tracking-wide">{t("photos.panel.title")}</h2>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-arc/50 bg-arc/[0.08] px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-arc">
          <Printer className="h-3 w-3" /> {t("photos.printsBadge")}
        </span>
        <span className="sd-numeric ml-auto text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
          {t("photos.count", { count: photos.length + pending.length, max: MAX_PHOTOS })}
        </span>
      </div>
      <p className="mt-3 border-t border-border pt-4 text-sm leading-relaxed text-muted-foreground">
        {t("photos.panel.body")}
      </p>

      {/* Drop target doubles as the primary button — one tap on a phone. */}
      <div
        onDragOver={(event: DragEvent) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event: DragEvent) => {
          event.preventDefault();
          setDragging(false);
          addFiles(event.dataTransfer.files);
        }}
        className={cn(
          "mt-5 rounded-xl border border-dashed px-5 py-7 text-center transition-colors",
          dragging ? "border-arc bg-arc/[0.06]" : "border-border-strong bg-black/20",
          full && "opacity-50",
        )}
      >
        <ImagePlus className="mx-auto h-6 w-6 text-muted-foreground" />
        <div className="mt-3 font-display text-xl tracking-wide">{t("photos.drop.title")}</div>
        <p className="mx-auto mt-1 max-w-sm text-sm leading-relaxed text-muted-foreground">
          {full ? t("photos.full") : t("photos.drop.body")}
        </p>
        <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            disabled={full}
            onClick={() => fileRef.current?.click()}
            className="inline-flex h-11 items-center gap-2 rounded-xl border border-arc px-5 font-mono text-[10px] uppercase tracking-[0.16em] text-arc hover:bg-arc/10 disabled:pointer-events-none disabled:opacity-40"
          >
            <ImagePlus className="h-4 w-4" /> {t("photos.drop.choose")}
          </button>
          {/* Touch devices (phone AND tablet) open the camera directly;
              genuine desktop falls back to a picker. Gated on the actual
              input mechanism (hover:hover + pointer:fine = a real mouse/
              trackpad), not screen width — a plain `sm:hidden` used to hide
              this on ANY viewport 640px+, which silently included every
              current tablet too (backlog MOBILE-1, found live testing on a
              real Android tablet: no way to snap a job-site photo directly
              from the device camera, only choose an existing one). Tablets
              have real cameras and get used in the field the same way
              phones do, so they should get this button too. */}
          <button
            type="button"
            disabled={full}
            onClick={() => (Capacitor.isNativePlatform() ? takeNativePhoto() : cameraRef.current?.click())}
            className="inline-flex h-11 items-center gap-2 rounded-xl border border-border-strong px-5 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground hover:border-arc hover:text-arc disabled:pointer-events-none disabled:opacity-40 [@media(hover:hover)_and_(pointer:fine)]:hidden"
          >
            <Camera className="h-4 w-4" /> {t("photos.drop.camera")}
          </button>
        </div>
        <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
          {t("photos.drop.hint", { max: MAX_PHOTOS })}
        </p>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            addFiles(event.target.files);
            event.target.value = "";
          }}
        />
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            addFiles(event.target.files);
            event.target.value = "";
          }}
        />
      </div>

      {photos.length > 0 || pending.length > 0 ? (
        <ul className="mt-5 grid gap-4">
          {photos.map((photo, index) => (
            <PhotoRow
              key={photo.id}
              photo={photo}
              index={index}
              count={photos.length}
              previewUrl={resolvedUrls[photo.id]}
              filenamePrefix={docFilenamePrefix || "photo"}
              onCaptionChange={(caption) => onChange(photos.map((p) => (p.id === photo.id ? { ...p, caption } : p)))}
              onMove={(delta) => {
                onChange(movePhoto(photos, index, delta));
                toast(t("photos.reordered"));
              }}
              onRemove={() => {
                onChange(photos.filter((p) => p.id !== photo.id));
                toast(t("photos.removed"));
              }}
            />
          ))}
          {pending.map((p) => (
            <PendingRow
              key={p.localId}
              pending={p}
              onRetry={() => {
                setPending((prev) => prev.filter((x) => x.localId !== p.localId));
                URL.revokeObjectURL(p.previewUrl);
                startUpload(p.file);
              }}
              onDismiss={() => {
                setPending((prev) => prev.filter((x) => x.localId !== p.localId));
                URL.revokeObjectURL(p.previewUrl);
              }}
            />
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function PhotoRow({
  photo,
  index,
  count,
  previewUrl,
  filenamePrefix,
  onCaptionChange,
  onMove,
  onRemove,
}: {
  photo: DocPhoto;
  index: number;
  count: number;
  previewUrl: string | undefined;
  filenamePrefix: string;
  onCaptionChange: (caption: string) => void;
  onMove: (delta: number) => void;
  onRemove: () => void;
}) {
  const t = useT();

  return (
    <li className="rounded-xl border border-border bg-black/20 p-3">
      <div className="flex gap-3">
        <div className="group relative h-24 w-24 shrink-0 overflow-hidden rounded-lg border border-border bg-black/30">
          {previewUrl ? (
            <img
              src={previewUrl}
              alt={photo.caption || t("photos.position", { n: index + 1 })}
              loading="lazy"
              className="h-full w-full object-cover"
            />
          ) : null}
          <span className="sd-numeric absolute left-0 top-0 rounded-br-lg bg-black/70 px-1.5 py-0.5 text-[10px] text-white">
            {index + 1}
          </span>
          {previewUrl ? <SaveToDeviceButton url={previewUrl} filename={`${filenamePrefix}-photo-${index + 1}.jpg`} /> : null}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1">
            <span className="sd-label">{t("photos.position", { n: index + 1 })}</span>
            <div className="ml-auto flex items-center gap-1">
              <IconButton label={t("photos.action.up")} disabled={index === 0} onClick={() => onMove(-1)}>
                <ChevronUp className="h-4 w-4" />
              </IconButton>
              <IconButton label={t("photos.action.down")} disabled={index === count - 1} onClick={() => onMove(1)}>
                <ChevronDown className="h-4 w-4" />
              </IconButton>
              <IconButton label={t("photos.action.remove")} onClick={onRemove}>
                <Trash2 className="h-4 w-4" />
              </IconButton>
            </div>
          </div>
          <input
            value={photo.caption}
            aria-label={t("photos.caption.label")}
            placeholder={t("photos.caption.placeholder")}
            onChange={(event) => onCaptionChange(event.target.value)}
            className="mt-2 h-10 w-full rounded-lg border border-border bg-black/30 px-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-arc"
          />
          <p className="mt-2 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
            <Printer className="h-3 w-3" /> {t("photos.printsBadge")}
          </p>
        </div>
      </div>
    </li>
  );
}

function PendingRow({
  pending,
  onRetry,
  onDismiss,
}: {
  pending: PendingUpload;
  onRetry: () => void;
  onDismiss: () => void;
}) {
  const t = useT();

  return (
    <li className="rounded-xl border border-border bg-black/20 p-3">
      <div className="flex gap-3">
        <div className="relative h-24 w-24 shrink-0 overflow-hidden rounded-lg border border-border">
          <img
            src={pending.previewUrl}
            alt=""
            className={cn(
              "h-full w-full object-cover",
              (pending.status === "uploading" || pending.status === "converting") && "opacity-50",
            )}
          />
        </div>
        <div className="min-w-0 flex-1">
          {pending.status === "uploading" ? (
            <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{t("photos.uploading")}</p>
          ) : pending.status === "converting" ? (
            <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{t("photos.converting")}</p>
          ) : (
            <>
              <p className="flex items-center gap-1.5 text-xs text-destructive">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                {pending.status === "unsupported" ? t("photos.unsupportedFormat") : t("photos.uploadError")}
              </p>
              <div className="mt-2 flex gap-2">
                {/* Retrying an unsupported format would just fail identically — only offer it for a real (network/Storage) failure. */}
                {pending.status === "error" ? (
                  <button
                    type="button"
                    onClick={onRetry}
                    className="rounded-md border border-border px-2.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.14em] text-muted-foreground hover:border-arc hover:text-arc"
                  >
                    {t("photos.action.retry")}
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={onDismiss}
                  className="rounded-md border border-border px-2.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.14em] text-muted-foreground hover:border-destructive hover:text-destructive"
                >
                  {t("photos.action.remove")}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </li>
  );
}

function IconButton({
  label,
  children,
  disabled,
  onClick,
}: {
  label: string;
  children: React.ReactNode;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border text-muted-foreground hover:border-arc hover:text-arc disabled:pointer-events-none disabled:opacity-30"
    >
      {children}
    </button>
  );
}

/**
 * "Save to device" — opens the native share sheet (native) or triggers a
 * plain browser download (web) for one photo. Sits on the tile's own
 * `relative` + `group` container (both PhotoRow above and
 * quote-sheet.tsx's PhotoSheetSection). Always visible on touch (no hover
 * state exists to reveal it there); hover-revealed on real desktop —
 * mirrors this app's existing hover:hover+pointer:fine convention (see the
 * camera-button comment in PhotoAttachments above) rather than inventing a
 * third interaction grammar for one action. `print:hidden` keeps it out of
 * the server-rendered PDF (quote-sheet.tsx's print-only route) regardless
 * of how a headless renderer happens to report hover/pointer capability.
 */
export function SaveToDeviceButton({ url, filename }: { url: string; filename: string }) {
  const t = useT();
  return (
    <button
      type="button"
      aria-label={t("photos.action.save")}
      title={t("photos.action.save")}
      onClick={(event) => {
        event.stopPropagation();
        void savePhotoToDevice(url, filename, () => toast.error(t("photos.saveError")));
      }}
      className="absolute right-1 top-1 inline-flex h-6 w-6 items-center justify-center rounded-md bg-black/70 text-white opacity-100 transition-opacity hover:bg-black/85 print:hidden [@media(hover:hover)_and_(pointer:fine)]:opacity-0 [@media(hover:hover)_and_(pointer:fine)]:group-hover:opacity-100"
    >
      <Share className="h-3.5 w-3.5" />
    </button>
  );
}
